import { Controller, Get, Header, Query, Res } from '@nestjs/common';
import { ApiOperation, ApiQuery, ApiResponse, ApiTags } from '@nestjs/swagger';
import type { Response } from 'express';
import { ALLOWED_WIDTHS, ImageDerivativeService } from './image-derivative.service';

/**
 * Resized WebP derivatives of uploaded images.
 *
 * Deliberately **unguarded**, like the static `/api/uploads` handler it sits
 * beside: these are the same public product photos any shopper already loads,
 * and requiring a token would mean no `<img>` on a storefront could use them.
 * The protections that matter here are the closed width allowlist and the
 * uploads-root containment check in the service, not authentication.
 */
@ApiTags('Images')
@Controller('img')
export class ImageDerivativeController {
  constructor(private readonly derivatives: ImageDerivativeService) {}

  @Get()
  @ApiOperation({ summary: 'Resized WebP derivative of an uploaded image' })
  @ApiQuery({ name: 'src', required: true, type: String, description: 'Path relative to the uploads root' })
  // Spread: ALLOWED_WIDTHS is a readonly tuple (that is what makes the width
  // type exhaustive), and Swagger's `enum` wants a mutable array.
  @ApiQuery({ name: 'w', required: true, enum: [...ALLOWED_WIDTHS] })
  @ApiResponse({ status: 200, description: 'WebP image bytes' })
  @ApiResponse({ status: 400, description: 'Bad path or unsupported width' })
  @ApiResponse({ status: 404, description: 'Source image not found' })
  // Immutable: the URL carries no version, but the cache key folds in the
  // source's mtime and size, so replacing an image changes the bytes served
  // under the same URL. A long max-age with revalidation is the honest setting
  // — a year would pin a stale picture in shopper caches after a re-upload.
  @Header('Cache-Control', 'public, max-age=86400, stale-while-revalidate=604800')
  async get(
    @Query('src') src: string,
    @Query('w') width: string,
    @Res() res: Response,
  ): Promise<void> {
    const { body, contentType } = await this.derivatives.get(src, Number(width));
    res.type(contentType).send(body);
  }
}
